// tslint:disable triple-equals
import React, { useEffect, useState } from 'react';
import ControlInput from 'lqa-ui/lib/Components/ControlInput';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import { getLocationInfo } from './apiCaller';
import 'react-datepicker/dist/react-datepicker.css';
import './styles.scss';
import Icon from 'lqa-ui/lib/Components/Icon';
import ControlSelect from 'lqa-ui/lib/Components/ControlSelect';
import { USER_LEVEL } from '../../utils/constants';
import { SearchBoxHotel } from './SearchBoxHotel';
import ControlTextarea from 'lqa-ui/lib/Components/ControlTextarea';
import { IHotelSelectOption } from '../../app/common/hooks/useSearchHotel';

const FormContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  row-gap: 1rem;
  margin-bottom: 20px;
  & svg.calendarIcon {
    width: 20px;
    height: 20px;
  }
  & svg.calendarIcon g path {
    stroke: #717171;
  }
  & .calendarIconContainer {
    top: 50%;
    transform: translateY(-50%);
  }
  & .hotel-search-input input {
    padding-top: 0;
  }
  & .hotel-search-input .has-value .control-input-holder {
    padding-top: 16px;
  }
  & .hotel-search-input .control-input-addon-front,
  & .hotel-search-input .control-input-addon {
    display: none;
  }
  & .cv-select .control-select-inner {
    border: 1px solid #e1e1e1;
    border-radius: 4px;
    padding: 8px 0;
    box-shadow: unset;
    font-size: 17px;
    color: grey;
    height: 40px;
  }
  & .cv-select.has-value .control-select-inner {
    height: 56px;
    padding: 8px 0 0;
  }
  & .cv-select.has-value .control-select-select {
    margin-top: 5px;
  }
  & .cv-select .control-select-inner .control-select-select.has-placeholder {
    font-size: 17px;
    color: grey;
  }
  & .cv-select.hasError .control-select-inner,
  & .hotel-search-input.hasError .control-input {
    box-shadow: inset 0 0 0 1px rgba(204, 48, 68, 0.3) !important;
    background-color: rgba(204, 48, 68, 0.07);
    border: unset;
  }
  & .cv-select .control-select-inner {
    box-shadow: unset !important;
  }
  & .cv-select.control-select.disabled .control-select-inner {
    color: rgba(51, 51, 51, 0.5);
  }
  & .cv-select.control-select .bp3-popover-dismiss-override {
    display: none;
  }
`;

const Input = ({ onChange, label, name, value, errors, ...otherProps }) => {
  return (
    <ControlInput
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      required={true}
      hasError={errors && !!errors[name]}
      {...otherProps}
    />
  );
};

const CVForm = ({
  isFormEditable = true,
  formTitle = 'Current Hotel CV Setup',
  departmentList,
  values,
  errors,
  handleChange,
  setErrors,
  index,
  setValues,
  showFormTitle = true,
  isAddingAnotherPosition = false,
  isPreviousHotelExperience = false,
  currentHotelInfo,
}) => {
  const [value, setValue] = useState(values[index]);
  const [hotelInputData, setHotelInputData] = useState<any>({
    label: '',
  });

  useEffect(() => {
    const temp: any = { ...hotelInputData };
    temp.label = values[index].hotelName ? values[index].hotelName : '';
    temp.value = values[index].hotelId ? values[index].hotelId : '';
    setHotelInputData(temp);
    setValue(values[index]);
  }, [values]);

  const extractKeys = (obj) => {
    let outputObj = {};
    Object.keys(obj).forEach((value, key) => {
      if (value === 'parent') {
        const extractedKeys = extractKeys(obj[value]);
        outputObj = { ...outputObj, ...extractedKeys };
      }
      if (value == 'type') {
        outputObj[obj[value]] = obj.name;
      }
    });
    return outputObj;
  };

  const handleHotelSelection = (locationId) => {
    getLocationInfo(locationId)
      .then((data) => {
        const extractedKeys: any = extractKeys(data);
        const newCvInputValues = [...values];
        if (extractedKeys.city) {
          newCvInputValues[index].city = extractedKeys.city;
        }
        if (extractedKeys.state) {
          newCvInputValues[index].state = extractedKeys.state;
        }
        if (extractedKeys.country) {
          newCvInputValues[index].country = extractedKeys.country;
        }

        setValues(newCvInputValues);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const isJobFieldDisabled = (key: string) => {
    if (
      index === 0 &&
      !isAddingAnotherPosition &&
      !isPreviousHotelExperience &&
      (!currentHotelInfo || !currentHotelInfo[key])
    ) {
      return false;
    }
    return !isAddingAnotherPosition && !isPreviousHotelExperience;
  };

  const onHotelSelection = (option: IHotelSelectOption[]) => {
    setHotelInputData(option[0]);
    const newCvInputValues = [...values];
    newCvInputValues[index].hotelName = option[0].text;
    newCvInputValues[index].hotelId = option[0].id;
    setValues(newCvInputValues);
    if (option && option[0].locationId) {
      handleHotelSelection(option[0].locationId);
      const newErrors = [...errors];
      if (newErrors[index]) {
        delete newErrors[index].city;
        delete newErrors[index].state;
        delete newErrors[index].country;
      }
      setErrors(newErrors);
    }
  };

  return (
    <div className="cv-form-container">
      <FormContainer>
        {showFormTitle && <div className="f-22 fw-600">{formTitle}</div>}
        {(!isAddingAnotherPosition ||
          (isPreviousHotelExperience && !isAddingAnotherPosition)) && (
          <>
            <div className="floating-wrapper">
              <SearchBoxHotel
                callBackSelectedOptions={onHotelSelection}
                disabled={
                  !isAddingAnotherPosition && !isPreviousHotelExperience
                }
              />
            </div>
            <Input
              label="Country"
              name="country"
              value={value.country}
              onChange={(e) => handleChange(e, index)}
              errors={errors[index]}
              disabled={value.hotelId}
            />
            <Input
              label="State"
              name="state"
              value={value.state}
              onChange={(e) => handleChange(e, index)}
              errors={errors[index]}
              disabled={value.hotelId}
            />
            <Input
              label="City"
              name="city"
              value={value.city}
              onChange={(e) => handleChange(e, index)}
              errors={errors[index]}
              disabled={value.hotelId}
            />
          </>
        )}
        {!!departmentList.length && (
          <div className="floating-wrapper">
            <label className={`${value.department ? 'hasValue' : ''}`}>
              Department
            </label>
            <ControlSelect
              disableSearch={true}
              onChange={(e) => {
                const newErrors = [...errors];
                if (!e.target.value) {
                  if (!newErrors[index]) {
                    newErrors[index] = {};
                  }
                  newErrors[index].department = `${'department'} is required`;
                } else {
                  if (newErrors[index]) {
                    delete newErrors[index].department;
                  }
                }
                setErrors(newErrors);
                const newCvInputValues = [...values];
                newCvInputValues[index].department = e.target.value;
                setValues(newCvInputValues);
              }}
              value={value.department}
              className={`cv-select ${
                errors[index] && errors[index].department ? 'hasError' : ''
              }`}
              disabled={isJobFieldDisabled('department')}
            >
              <option />
              <option key="All" value="All">
                All
              </option>
              {departmentList.map((department) => {
                return (
                  <option key={department.id} value={department.value}>
                    {department.value}
                  </option>
                );
              })}
            </ControlSelect>
          </div>
        )}
        <Input
          label="Job Title"
          name="currentJobTitle"
          value={value.currentJobTitle}
          onChange={(e) => handleChange(e, index)}
          errors={errors[index]}
          disabled={isJobFieldDisabled('currentJobTitle')}
        />
        <div className="floating-wrapper">
          <label className={`${value.level ? 'hasValue' : ''}`}>
            Job Level
          </label>
          <ControlSelect
            disableSearch={true}
            onChange={(e) => {
              const newErrors = [...errors];
              if (!e.target.value) {
                if (!newErrors[index]) {
                  newErrors[index] = {};
                }
                newErrors[index].level = `${'level'} is required`;
              } else {
                if (newErrors[index]) {
                  delete newErrors[index].level;
                }
              }
              setErrors(newErrors);
              const newCvInputValues = [...values];
              newCvInputValues[index].level = e.target.value;
              setValues(newCvInputValues);
            }}
            value={value.level}
            className={`cv-select ${
              errors[index] && errors[index].level ? 'hasError' : ''
            }`}
            disabled={isJobFieldDisabled('level')}
          >
            <option />
            {Object.entries(USER_LEVEL).map(([key, value], index) => {
              return (
                <option key={index} value={key}>
                  {value}
                </option>
              );
            })}
          </ControlSelect>
        </div>
        <div
          className={`formDiv inpDate ${
            errors[index] && errors[index].startDate ? 'hasError' : ''
          }`}
        >
          <label className={`${value.startDate ? 'hasValue' : ''}`}>
            Start Date
          </label>
          <div className="calendarIconContainer position-absolute right-10">
            <Icon
              kind="calendar-icon"
              height={5}
              width={5}
              className="calendarIcon"
            />
          </div>
          <DatePicker
            name="startDate"
            popperClassName="date-picker-popper"
            maxDate={new Date()}
            selected={value.startDate ? new Date(value.startDate) : ''}
            onFocus={(e) => e.target.blur()}
            onKeyDown={(e) => {
              if (isNaN(e.target.value) || e.target.value !== '/') {
                e.preventDefault();
              }
            }}
            onChange={(selectedDate) => {
              const newCvInputValues = [...values];
              newCvInputValues[index].startDate = selectedDate;
              if (selectedDate) {
                const newErrors = [...errors];
                if (newErrors[index]) {
                  delete newErrors[index].startDate;
                }
                setErrors(newErrors);

                // if endDate is greater than start Date, reset end date
                if (value.endDate && selectedDate > value.endDate) {
                  newCvInputValues[index].endDate = null;
                }
              } else {
                const newErrors: any = [...errors];
                if (!newErrors[index]) {
                  newErrors[index] = {};
                }
                newErrors[index].startDate = `${'startDate'} is required`;
                setErrors(newErrors);
              }
              setValues(newCvInputValues);
            }}
            showMonthDropdown={true}
            showYearDropdown={true}
            scrollableYearDropdown={true}
            scrollableMonthYearDropdown={true}
            yearDropdownItemNumber={100}
          />
        </div>
        {(isAddingAnotherPosition || isPreviousHotelExperience) && (
          <div
            className={`formDiv inpDate ${
              errors[index] && errors[index].endDate ? 'hasError' : ''
            }`}
          >
            <label className={`${value.endDate ? 'hasValue' : ''}`}>
              End Date
            </label>
            <div className="calendarIconContainer position-absolute right-10">
              <Icon
                kind="calendar-icon"
                height={5}
                width={5}
                className="calendarIcon"
              />
            </div>
            <DatePicker
              name="endDate"
              popperClassName="date-picker-popper"
              selected={value.endDate ? new Date(value.endDate) : ''}
              onFocus={(e) => e.target.blur()}
              onKeyDown={(e) => {
                if (isNaN(e.target.value) || e.target.value !== '/') {
                  e.preventDefault();
                }
              }}
              minDate={value.startDate}
              maxDate={new Date()}
              onChange={(selectedDate) => {
                const newCvInputValues = [...values];
                newCvInputValues[index].endDate = selectedDate;
                setValues(newCvInputValues);
                if (selectedDate) {
                  const newErrors = [...errors];
                  if (newErrors[index]) {
                    delete newErrors[index].endDate;
                  }
                  setErrors(newErrors);
                } else {
                  const newErrors: any = [...errors];
                  if (!newErrors[index]) {
                    newErrors[index] = {};
                  }
                  newErrors[index].endDate = `${'endDate'} is required`;
                  setErrors(newErrors);
                }
              }}
              showMonthDropdown={true}
              showYearDropdown={true}
              scrollableYearDropdown={true}
              scrollableMonthYearDropdown={true}
              yearDropdownItemNumber={100}
            />
          </div>
        )}
        <div className="floating-wrapper">
          <label className={`${value.jobDescription ? 'hasValue' : ''}`}>
            Job Responsibilities
          </label>
          <ControlTextarea
            minRows={2}
            className={`${errors[index] && errors[index].jobDescription ? 'has-error' : ''}`}
            name="jobDescription"
            value={value.jobDescription}
            onChange={(e) => handleChange(e, index)}
            disabled={!isFormEditable}
          />
        </div>
      </FormContainer>
    </div>
  );
};

export default CVForm;
